import React, { useMemo, useState } from 'react';
import { createCn } from '../../../helpers/utils';
import classes from '../styles.module.scss';
import { SummaryStructured } from '../../../services/api.service';
import {
  extractBriefSummary,
  splitSummaryPoints,
  stripMarkdown,
} from '../../../utils/sanitize-content';
import { highlightTechTerms } from '../../../utils/tech-terms';
import { normalizeSpeakerNamesInText } from '../../../utils/speaker-display-names';

const cn = createCn('file', classes);

type Props = {
  summary: string;
  structured?: SummaryStructured | null;
  meetingName?: string;
  duration?: string;
  editing?: boolean;
  editValue?: string;
  onEditChange?: (value: string) => void;
};

function parseTopicLine(line: string): { title: string; body: string } {
  const cleaned = stripMarkdown(normalizeSpeakerNamesInText(line.replace(/^[•\-*]\s*/, '').trim()));
  const colon = cleaned.indexOf(':');
  if (colon > 0 && colon < 80) {
    return {
      title: cleaned.slice(0, colon).trim(),
      body: cleaned.slice(colon + 1).trim(),
    };
  }
  return { title: cleaned, body: '' };
}

function parseActionItem(item: string): { task: string; assignee: string } {
  const clean = stripMarkdown(normalizeSpeakerNamesInText(item));
  const parts = clean.split(/\s*[—–-]\s+/);
  const cap = (s: string) => {
    const t = s.trim();
    return t ? t.charAt(0).toUpperCase() + t.slice(1) : t;
  };
  if (parts.length >= 2) {
    return { task: cap(parts[0]), assignee: parts.slice(1).join(' — ').trim() };
  }
  return { task: cap(clean.trim()), assignee: '—' };
}

function AccordionSection({
  title,
  defaultOpen = true,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className={cn('summary-accordion')}>
      <button type="button" className={cn('summary-accordion-head')} onClick={() => setOpen(!open)}>
        <span>{title}</span>
        <span className={cn('summary-accordion-icon', { open })}>▼</span>
      </button>
      {open && <div className={cn('summary-accordion-body')}>{children}</div>}
    </section>
  );
}

function SummaryHeader({ meetingName, duration }: { meetingName?: string; duration?: string }) {
  const subtitle = [meetingName, duration].filter(Boolean).join(' · ');
  return (
    <header className={cn('summary-hero')}>
      <div className={cn('summary-hero-badge')}>Конспект встречи</div>
      {subtitle && <h1 className={cn('summary-hero-title')}>{subtitle}</h1>}
    </header>
  );
}

function BriefSummaryTable({ text }: { text: string }) {
  const points = useMemo(() => splitSummaryPoints(text), [text]);
  if (!points.length) {
    return <p className={cn('empty')}>Краткое резюме недоступно.</p>;
  }
  return (
    <table className={cn('summary-brief-table')}>
      <thead>
        <tr>
          <th>№</th>
          <th>Основной тезис</th>
        </tr>
      </thead>
      <tbody>
        {points.map((point, i) => (
          <tr key={i}>
            <td>{i + 1}</td>
            <td dangerouslySetInnerHTML={{ __html: highlightTechTerms(stripMarkdown(point)) }} />
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const SummaryView = ({
  summary,
  structured,
  meetingName,
  duration,
  editing = false,
  editValue = '',
  onEditChange,
}: Props) => {
  const data = useMemo(() => {
    if (structured && (structured.summary || structured.key_topics?.length)) {
      return structured;
    }
    return null;
  }, [structured]);

  const briefText = useMemo(() => {
    const raw = data?.summary || summary || '';
    return normalizeSpeakerNamesInText(extractBriefSummary(raw));
  }, [data, summary]);

  if (!summary?.trim() && !data && !editing) {
    return <p className={cn('empty')}>Конспект ещё не сгенерирован.</p>;
  }

  if (editing) {
    return (
      <div className={cn('section-edit-area')}>
        <p className={cn('section-edit-hint')}>
          Редактирование текста конспекта. Структурированные блоки обновятся после повторной генерации AI.
        </p>
        <textarea
          className={cn('section-edit-textarea', { summary: true })}
          value={editValue}
          onChange={(e) => onEditChange?.(e.target.value)}
          placeholder="Текст конспекта…"
          spellCheck
        />
      </div>
    );
  }

  if (!summary?.trim() && !data) {
    return <p className={cn('empty')}>Конспект ещё не сгенерирован.</p>;
  }

  if (data) {
    const topics = (data.key_topics || []).map(parseTopicLine);
    const actions = (data.action_items || []).map(parseActionItem);

    return (
      <article className={cn('summary-panel')}>
        <SummaryHeader meetingName={meetingName} duration={duration} />

        <div className={cn('summary-body')}>
          {briefText && (
            <AccordionSection title="Краткое резюме встречи">
              <BriefSummaryTable text={briefText} />
            </AccordionSection>
          )}

          {topics.length > 0 && (
            <AccordionSection title="Ключевые темы">
              <div className={cn('summary-chips')}>
                {topics.map((t, i) => (
                  <div key={i} className={cn('summary-chip')}>
                    <span className={cn('summary-chip-label')}>{t.title}</span>
                    {t.body && (
                      <p
                        className={cn('summary-chip-body')}
                        dangerouslySetInnerHTML={{ __html: highlightTechTerms(t.body) }}
                      />
                    )}
                  </div>
                ))}
              </div>
            </AccordionSection>
          )}

          {(data.decisions || []).length > 0 && (
            <AccordionSection title="Принятые решения" defaultOpen={false}>
              <table className={cn('summary-table')}>
                <thead><tr><th>№</th><th>Решение</th></tr></thead>
                <tbody>
                  {(data.decisions || []).map((d, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td dangerouslySetInnerHTML={{ __html: highlightTechTerms(stripMarkdown(normalizeSpeakerNamesInText(d))) }} />
                    </tr>
                  ))}
                </tbody>
              </table>
            </AccordionSection>
          )}

          {actions.length > 0 && (
            <AccordionSection title="Задачи и поручения" defaultOpen={false}>
              <table className={cn('summary-table')}>
                <thead><tr><th>№</th><th>Задача</th><th>Ответственный</th></tr></thead>
                <tbody>
                  {actions.map((a, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td dangerouslySetInnerHTML={{ __html: highlightTechTerms(a.task) }} />
                      <td>{a.assignee}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </AccordionSection>
          )}

          {(data.questions || []).length > 0 && (
            <AccordionSection title="Открытые вопросы" defaultOpen={false}>
              <ul className={cn('summary-list')}>
                {(data.questions || []).map((q, i) => (
                  <li key={i} dangerouslySetInnerHTML={{ __html: highlightTechTerms(stripMarkdown(q)) }} />
                ))}
              </ul>
            </AccordionSection>
          )}
        </div>
      </article>
    );
  }

  return (
    <article className={cn('summary-panel')}>
      <SummaryHeader meetingName={meetingName} duration={duration} />
      <div className={cn('summary-body')}>
        {briefText && (
          <AccordionSection title="Краткое резюме встречи">
            <BriefSummaryTable text={briefText} />
          </AccordionSection>
        )}
      </div>
    </article>
  );
};

export default SummaryView;
